import type { I18nFn } from '#/i18n/types/I18nFn';
import type { OrgDashboardApiCartLineItem } from '#/modules/orgDashboard/api/types/OrgDashboardApiCartLineItem';
import type { OrgDashboardApiCartInvoice } from '#/modules/orgDashboard/api/types/OrgDashboardApiInvoice';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import type {
  BaseCheckoutAnalyticsItem,
  CheckoutAnalyticsItem,
  CheckoutAnalyticsItemPurchaseMethod,
  CheckoutItemEcommerceAnalyticsActionKey,
} from '#/modules/orgDashboard/types/analytics/CheckoutAnalyticsItem';

import { trackEvent } from './analytics/track/trackEvent';
import { PAYMENT_TYPES } from './constants/general/paymentTypes';
import { PAYMENT_TYPES_STAFF } from './constants/general/paymentTypesStaff';

export function getStaffPaymentTypeName(typeId: string) {
  const type = PAYMENT_TYPES_STAFF.find((t) => t.id === typeId);
  return type ? type.name : '';
}

export function getPaymentTypeName(i18n: I18nFn, typeId: string) {
  const type = PAYMENT_TYPES(i18n).find((t) => t.id === typeId);
  return type ? type.name : '';
}

function getAnalyticsForLineItem(
  lineItem: OrgDashboardApiCartLineItem,
  org: OrgDashboardApiOrg,
  actionKey: CheckoutItemEcommerceAnalyticsActionKey,
): CheckoutAnalyticsItem {
  const currencyCode = 'USD';

  const baseData: Omit<BaseCheckoutAnalyticsItem, 'ecommerce'> = {
    org_name: org.name,
    org_id: org.id,
  };

  switch (lineItem.itemType) {
    case 'CREDITS':
      return {
        ...baseData,
        product_type: 'credit pack',
        pack_id: lineItem.itemTypeId,
        ecommerce: {
          currencyCode,
          [actionKey]: {
            products: [
              {
                name: lineItem.description,
                id: lineItem.itemTypeId,
                price: lineItem.unitPrice,
                brand: 'Idealist',
                category: 'credit pack',
                quantity: 1,
              },
            ],
          },
        },
      };

    case 'JOB':
      return {
        ...baseData,
        product_type: 'listing',
        listing_type: 'job',
        listing_id: lineItem.listingId,
        name: lineItem.listingName,
        ecommerce: {
          currencyCode,
          [actionKey]: {
            products: [
              {
                name: 'job listing',
                id: lineItem.listingId,
                price: lineItem.unitPrice,
                brand: org.name,
                category: 'listing',
                variant: lineItem.description,
                quantity: 1,
              },
            ],
          },
        },
      };

    case 'INTERNSHIP':
      return {
        ...baseData,
        product_type: 'listing',
        listing_type: 'internship',
        listing_id: lineItem.listingId,
        name: lineItem.listingName,
        ecommerce: {
          currencyCode,
          [actionKey]: {
            products: [
              {
                name: 'internship listing',
                id: lineItem.listingId,
                price: lineItem.unitPrice,
                brand: org.name,
                category: 'listing',
                variant: lineItem.description,
                quantity: 1,
              },
            ],
          },
        },
      };
    case 'LISTING_PROMOTION':
      return {
        ...baseData,
        product_type: 'listing promotion',
        listing_type: 'volop',
        listing_id: lineItem.listingId,
        name: lineItem.listingName,
        ecommerce: {
          currencyCode,
          [actionKey]: {
            products: [
              {
                name: 'volunteer opportunity promotion',
                id: lineItem.listingId,
                price: lineItem.unitPrice,
                brand: org.name,
                category: 'listing promotion',
                variant: lineItem.description,
                quantity: 1,
              },
            ],
          },
        },
      };
    case 'MEMBERSHIP':
      return {
        ...baseData,
        product_type: 'membership',
        membership_type: lineItem.itemTypeId,
        name: 'org membership',
        ecommerce: {
          currencyCode,
          [actionKey]: {
            products: [
              {
                name: 'membership',
                id: lineItem.id,
                price: lineItem.unitPrice,
                brand: org.name,
                category: 'membership',
                variant: lineItem.description,
                quantity: 1,
              },
            ],
          },
        },
      };
  }
}

export function trackRemovedFromCart(
  lineItem: OrgDashboardApiCartLineItem,
  org: OrgDashboardApiOrg,
) {
  const data = getAnalyticsForLineItem(lineItem, org, 'remove');

  if (data) {
    trackEvent('Success: Removed from Cart', data);
  }
}

export function trackPurchased(
  invoice: OrgDashboardApiCartInvoice,
  org: OrgDashboardApiOrg,
  method: CheckoutAnalyticsItemPurchaseMethod,
) {
  const paymentMethodMapping = {
    card: 'Credit Card',
    check: 'Check',
    credits: 'Credits',
  };

  invoice.lineItems.forEach((lineItem) => {
    const baseData = getAnalyticsForLineItem(lineItem, org, 'purchase');
    if (!baseData) return;

    const data: CheckoutAnalyticsItem = {
      ...baseData,
      method,
      ecommerce: {
        ...baseData.ecommerce,
        purchase: {
          products: baseData.ecommerce.purchase?.products ?? [],
          actionField: {
            id: invoice.id,
            affiliation: `Payment: ${paymentMethodMapping[method]}`,
            revenue: invoice.due,
            shipping: '0',
          },
        },
      },
    };

    trackEvent(
      `Success: Purchase - Paid ${paymentMethodMapping[method]}`,
      data,
    );
  });
}
